import { useCallback, useRef, useState } from "react";
import { markVideoViewed } from "services/on-board";

const COMPLETION_THRESHOLD_SECONDS = 7;
const RAGE_CLICK_COUNT = 3;
const RAGE_CLICK_WINDOW_MS = 5000;
const IN_FLIGHT_TIMEOUT_MS = 15000; // Safety: clear in-flight after 15s max

/**
 * Shared hook for REST-based video completion with retry + fallback modal.
 * Used by both onboarding and deeper-dive screens.
 *
 * Resiliency guarantees:
 * 1. Auto-completes when video reaches last 7 seconds
 * 2. onEnd fires as safety net if auto-complete missed
 * 3. REST call retries once after 2s on failure
 * 4. Fallback modal shows if both REST attempts fail
 * 5. Fallback modal "Mark as Viewed" advances locally even if REST fails
 * 6. Rage click detection (3x on disabled button) triggers fallback
 * 7. in-flight lock auto-clears after 15s to prevent permanent blocking
 * 8. Local state always advances — server sync is best-effort
 */
export function useVideoCompletion() {
  const [showFallbackModal, setShowFallbackModal] = useState(false);
  const [fallbackVideoId, setFallbackVideoId] = useState<string | null>(null);
  const [fallbackLoading, setFallbackLoading] = useState(false);

  const completionSentRef = useRef(new Set<string>());
  const completionTriggeredRef = useRef(new Set<string>()); // Tracks first trigger — prevents repeated handleVideoCompleted calls
  const inFlightRef = useRef(new Set<string>());
  const inFlightTimerRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const videoDurationRef = useRef<Record<string, number>>({});
  const timeSpentRef = useRef<Record<string, number>>({});
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rageClickRef = useRef<number[]>([]);

  /** Clear the in-flight lock for a video */
  const clearInFlight = useCallback((expertVoiceId: string) => {
    inFlightRef.current.delete(expertVoiceId);
    if (inFlightTimerRef.current[expertVoiceId]) {
      clearTimeout(inFlightTimerRef.current[expertVoiceId]);
      delete inFlightTimerRef.current[expertVoiceId];
    }
  }, []);

  /**
   * Send video completion via REST. Retries once after 2s.
   * If both attempts fail, shows the fallback modal.
   */
  const sendCompletion = useCallback(
    async (expertVoiceId: string, retryCount = 0) => {
      // Already confirmed by server — skip
      if (completionSentRef.current.has(expertVoiceId)) return;
      // Already in-flight — skip (but in-flight auto-clears after 15s)
      if (inFlightRef.current.has(expertVoiceId)) return;

      inFlightRef.current.add(expertVoiceId);
      // Safety: auto-clear in-flight after 15s to prevent permanent blocking
      inFlightTimerRef.current[expertVoiceId] = setTimeout(() => {
        console.warn("[video] In-flight timeout — clearing lock", { expertVoiceId });
        clearInFlight(expertVoiceId);
      }, IN_FLIGHT_TIMEOUT_MS);

      const timeSpent = timeSpentRef.current[expertVoiceId] || 0;
      console.log("[video] Sending REST completion", {
        expertVoiceId,
        timeSpent,
        retry: retryCount,
      });

      try {
        await markVideoViewed(expertVoiceId, timeSpent);
        console.log("[video] REST completion success", { expertVoiceId });
        completionSentRef.current.add(expertVoiceId);
        clearInFlight(expertVoiceId);
      } catch (error) {
        console.error("[video] REST completion failed", {
          expertVoiceId,
          retry: retryCount,
          error,
        });

        if (retryCount === 0) {
          // Clear in-flight so retry can re-acquire
          clearInFlight(expertVoiceId);
          retryTimeoutRef.current = setTimeout(
            () => sendCompletion(expertVoiceId, 1),
            2000
          );
        } else {
          // Both attempts failed. The caller has already advanced the user
          // locally via handleVideoCompleted, so don't interrupt them with a
          // modal. Leave the triggered flag set so subsequent onProgress
          // ticks don't kick off a fresh 2-attempt cycle on every frame —
          // the explicit fallback paths (rage click / manual button) remain
          // the routes that surface the modal, and the next page load will
          // pick up is_previewed from the server.
          clearInFlight(expertVoiceId);
          console.warn(
            "[video] REST completion gave up after retry — local state already advanced",
            { expertVoiceId }
          );
        }
      }
    },
    [clearInFlight]
  );

  /**
   * Call from onProgress — computes duration and auto-marks near end.
   * Returns true if the video was just marked complete (caller should advance).
   */
  const checkAutoComplete = useCallback(
    (
      expertVoiceId: string,
      playedSeconds: number,
      playedFraction: number
    ): boolean => {
      // Track time
      timeSpentRef.current[expertVoiceId] = playedSeconds;

      // Compute actual video duration from played fraction
      if (playedFraction > 0.01 && !videoDurationRef.current[expertVoiceId]) {
        const computed = playedSeconds / playedFraction;
        if (computed > 10) {
          videoDurationRef.current[expertVoiceId] = computed;
          console.log("[video] Duration computed", {
            expertVoiceId,
            duration: computed,
          });
        }
      }

      // Auto-mark when near end — only trigger ONCE per video
      const duration = videoDurationRef.current[expertVoiceId];
      if (
        duration &&
        duration > 10 &&
        playedSeconds >= duration - COMPLETION_THRESHOLD_SECONDS &&
        !completionTriggeredRef.current.has(expertVoiceId)
      ) {
        completionTriggeredRef.current.add(expertVoiceId);
        console.log("[video] Near end — auto-completing", {
          expertVoiceId,
          playedSeconds,
          duration,
        });
        sendCompletion(expertVoiceId);
        return true;
      }

      return false;
    },
    [sendCompletion]
  );

  /** Manually trigger the fallback modal from a "Having issues?" link */
  const showManualFallback = useCallback(
    (expertVoiceId: string) => {
      console.log("[video] Manual fallback triggered", { expertVoiceId });
      setFallbackVideoId(expertVoiceId);
      setShowFallbackModal(true);
    },
    [sendCompletion]
  );

  /**
   * Call from onEnd — safety net if auto-complete didn't fire.
   * Sends completion if not already triggered.
   */
  const ensureCompletion = useCallback(
    (expertVoiceId: string): void => {
      if (
        !completionSentRef.current.has(expertVoiceId) &&
        !completionTriggeredRef.current.has(expertVoiceId)
      ) {
        completionTriggeredRef.current.add(expertVoiceId);
        sendCompletion(expertVoiceId);
      }
    },
    [sendCompletion]
  );

  /** Track rage clicks on disabled buttons. Returns true if modal should show. */
  const trackRageClick = useCallback(
    (currentVideoId: string | undefined): boolean => {
      if (!currentVideoId) return false;

      const now = Date.now();
      rageClickRef.current.push(now);
      rageClickRef.current = rageClickRef.current.filter(
        (t) => now - t < RAGE_CLICK_WINDOW_MS
      );

      if (rageClickRef.current.length >= RAGE_CLICK_COUNT) {
        console.log("[video] Rage clicks detected — showing fallback modal");
        rageClickRef.current = [];
        setFallbackVideoId(currentVideoId);
        setShowFallbackModal(true);
        return true;
      }
      return false;
    },
    []
  );

  /**
   * Handle "Mark as Viewed" from the fallback modal.
   * ALWAYS advances locally even if REST fails — user is never stuck.
   */
  const handleFallbackMark = useCallback(
    async (onMarked: (videoId: string) => void) => {
      if (!fallbackVideoId) return;
      setFallbackLoading(true);

      try {
        const timeSpent = timeSpentRef.current[fallbackVideoId] || 0;
        await markVideoViewed(fallbackVideoId, timeSpent);
        completionSentRef.current.add(fallbackVideoId);
      } catch {
        console.warn(
          "[video] Fallback REST also failed — advancing locally anyway"
        );
      }

      // ALWAYS advance locally — this is the last resort, never block the user
      onMarked(fallbackVideoId);
      setShowFallbackModal(false);
      setFallbackVideoId(null);
      setFallbackLoading(false);
    },
    [fallbackVideoId]
  );

  /** Close modal without action. */
  const dismissFallback = useCallback(() => {
    setShowFallbackModal(false);
    setFallbackVideoId(null);
  }, []);

  /** Call on unmount to clear all pending timers. */
  const cleanup = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    Object.values(inFlightTimerRef.current).forEach(clearTimeout);
    inFlightTimerRef.current = {};
  }, []);

  return {
    showFallbackModal,
    fallbackLoading,
    sendCompletion,
    checkAutoComplete,
    showManualFallback,
    ensureCompletion,
    trackRageClick,
    handleFallbackMark,
    dismissFallback,
    cleanup,
    completionSentRef,
  };
}
